// Libraries
import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Text } from '@chakra-ui/react';
// Components
import { FooterCellLink, RatingHeader, SummaryCellTable, VesselSegmentCell } from './SummaryTable.cells';
// Types
import { SummaryColumnsBuilderParams, SummaryTableBody } from './SummaryTable.types';
// Constants
import { Rating } from '@constants/index';

const ratings = Object.values(Rating);

/**
 * Builds an array of column definitions for the summary table.
 *
 * @param {SummaryColumnsBuilderParams} params - The parameters for building the columns.
 * @param {Ratings} params.eoyRatingTotal - The end-of-year ratings total.
 * @param {Ratings} params.ytdRatingTotal - The year-to-date ratings total..
 * @param {number} params.total - Total of vessels.
 * @returns {ColumnDef<SummaryTableBody>[]} An array of column definitions.
 */
export const summaryColumnsBuilder = ({
  eoyRatingTotal,
  ytdRatingTotal,
  total,
}: SummaryColumnsBuilderParams): ColumnDef<SummaryTableBody>[] => {
  const lastCell = ratings.length - 1;

  // Map each rating to a column definition
  const ratingsColumns = ratings.map<ColumnDef<SummaryTableBody>>((rating, index) => {
    const pl = index === 0 ? 1 : 0;
    const pr = lastCell === index ? 1 : 0;
    const borderStartRadius = index === 0 ? 'base' : 'none';
    const borderEndRadius = lastCell === index ? 'base' : 'none';

    return {
      header: () => <RatingHeader rating={rating} pl={pl} pr={pr} />,
      accessorKey: rating,
      footer: () => (
        <FooterCellLink
          rating={rating}
          eoyRating={eoyRatingTotal[rating]}
          ytdRating={ytdRatingTotal[rating]}
          pl={pl}
          pr={pr}
          borderStartRadius={borderStartRadius}
          borderEndRadius={borderEndRadius}
        />
      ),
      cell: ({ row: { original } }) => (
        <SummaryCellTable
          pl={pl}
          pr={pr}
          eoyRating={original.eoyRating[rating]}
          ytdRating={original.ytdRating[rating]}
          borderStartRadius={borderStartRadius}
          borderEndRadius={borderEndRadius}
        />
      ),
    };
  });

  /**
   * Column definition for the vessel segment column in the summary table.
   *
   * @type {ColumnDef<SummaryTableBody>}
   */
  const vesselSegmentColumn: ColumnDef<SummaryTableBody> = {
    header: () => (
      <Text fontSize='s' fontWeight='medium' minW={28} p={2}>
        Vessel Segment
      </Text>
    ),
    footer: () => (
      <Text fontSize='hs' fontWeight='medium' p={2}>
        Total: {total}
      </Text>
    ),
    accessorKey: 'vesselType',
    cell: ({ row: { original } }) => <VesselSegmentCell vesselType={original.type} total={original.total || 0} />,
  };

  return [vesselSegmentColumn, ...ratingsColumns];
};
